import { useState, useEffect } from 'react';
import teamService from '../services/teamService';
import { notify } from '../components/CustomToast';

export const useTeams = () => {
    const [teams, setTeams] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    const fetchTeams = async () => {
        try {
            setLoading(true);
            const response = await teamService.list();
            setTeams(response.data.teams || []);
            setError(null);
        } catch (err) {
            setError(err.message || 'Failed to fetch teams');
            notify('error', 'Failed to load teams');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchTeams();
    }, []);

    const createTeam = async (data) => {
        try {
            const response = await teamService.create(data);
            notify('success', 'Team created successfully');
            await fetchTeams();
            return response.data.team;
        } catch (err) {
            notify('error', err.message || 'Failed to create team');
            throw err;
        }
    };

    return { teams, loading, error, refetch: fetchTeams, createTeam };
};

export const useTeam = (teamId) => {
    const [team, setTeam] = useState(null);
    const [members, setMembers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    const fetchTeam = async () => {
        if (!teamId) return;

        try {
            setLoading(true);
            const [teamResponse, membersResponse] = await Promise.all([
                teamService.show(teamId),
                teamService.members(teamId),
            ]);
            setTeam(teamResponse.data.team);
            setMembers(membersResponse.data.members || []);
            setError(null);
        } catch (err) {
            setError(err.message || 'Failed to fetch team');
            notify('error', 'Failed to load team details');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchTeam();
    }, [teamId]);

    const updateTeam = async (data) => {
        try {
            const response = await teamService.update(teamId, data);
            setTeam(response.data.team);
            notify('success', 'Team updated successfully');
            return response.data.team;
        } catch (err) {
            notify('error', err.message || 'Failed to update team');
            throw err;
        }
    };

    const inviteMember = async (email, role = 'member') => {
        try {
            await teamService.invite(teamId, { email, role });
            notify('success', `Invitation sent to ${email}`);
        } catch (err) {
            notify('error', err.message || 'Failed to send invitation');
            throw err;
        }
    };

    const removeMember = async (userId) => {
        try {
            await teamService.removeMember(teamId, userId);
            notify('success', 'Member removed successfully');
            await fetchTeam();
        } catch (err) {
            notify('error', err.message || 'Failed to remove member');
            throw err;
        }
    };

    const changeRole = async (userId, role) => {
        try {
            await teamService.changeRole(teamId, userId, { role });
            notify('success', 'Role updated successfully');
            await fetchTeam();
        } catch (err) {
            notify('error', err.message || 'Failed to update role');
            throw err;
        }
    };

    return {
        team,
        members,
        loading,
        error,
        refetch: fetchTeam,
        updateTeam,
        inviteMember,
        removeMember,
        changeRole,
    };
};
